import { useTranslation } from 'react-i18next';
import { useMeQuery, usePlatformUsersQuery, useSetGlobalRoleMutation } from '@platform/state/api';
import type { IPlatformUser } from '@platform/dao';
import { initials } from '../../shell/initials';
import { SysNote } from '../system/SysNote';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { useFailToast } from '../system/useFailToast';
import { globalRoleKey, globalRoles } from './globalRoles';

const COLUMNS = '28px minmax(180px, 1.4fr) minmax(200px, 1.6fr) 200px';

/**
 * «Глобальні ролі» (прототип v4 G:L8073–8146, pgAdmGroles): ролі платформи понад роль у просторі — три картки з описом і кількістю облікових
 * записів, таблиця всіх облікових записів платформи (GET /platform/users: особа, ел. пошта, глобальна роль). Роль змінює лише адміністратор
 * платформи (PUT …/global-role, останнього адміністратора сервер не знімає); іншим — «Лише адміністратори платформи можуть змінювати…».
 */
export function AdminGlobalRolesPage() {
  const { t } = useTranslation();
  const { data: me } = useMeQuery();
  const { data: fetched } = usePlatformUsersQuery();
  const [setRole] = useSetGlobalRoleMutation();
  const fail = useFailToast();
  const canEdit = me?.globalRole === 'Platform admin';
  const self: IPlatformUser[] = me ? [{ id: me.id, name: me.displayName, initials: initials(me.displayName), email: me.email ?? null, kind: 'staff', globalRole: me.globalRole }] : [];
  const users = fetched ?? self;
  const roleLabel = (role: string) => t(`sysAdmin.global.${globalRoleKey(role)}`);
  const rows = users.map((u) => ({
    key: u.id,
    cells: [
      <span key="a" className="sp-avatar">{u.initials}</span>,
      <span key="n" className="sp-who"><span className="sp-strong ad-ellipsis">{u.name}</span>{u.id === me?.id && <span className="sp-badge">{t('sysAdmin.global.you')}</span>}</span>,
      <span key="e" className="sp-muted">{u.email ?? ''}</span>,
      <select key="r" className="sp-select ad-grole__select" value={u.globalRole} disabled={!canEdit} aria-label={t('sysAdmin.global.roleOf', { name: u.name })}
        onChange={(e) => setRole({ userId: u.id, globalRole: e.target.value }).unwrap().catch(fail)}>
        {globalRoles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
      </select>,
    ],
  }));
  return (
    <SystemPage title={t('sysPages.sections.global-roles')} sub={t('sysAdmin.global.sub')}>
      {me && !canEdit && <SysNote tone="alert">{t('sysAdmin.global.locked')}</SysNote>}
      <div className="sp-cards">
        {globalRoles.map((r) => (
          <div key={r} className="sp-card ad-grole">
            <span className="ad-grole__top"><span className="sp-card__title">{roleLabel(r)}</span><span className="sp-mono sp-muted">{users.filter((u) => u.globalRole === r).length}</span></span>
            <span className="sp-card__desc">{t(`sysAdmin.global.desc.${globalRoleKey(r)}`)}</span>
          </div>
        ))}
      </div>
      <div className="ad-groles">
        <SysTable columns={COLUMNS} minWidth={680} head={['', t('sysAdmin.global.person'), t('sysAdmin.global.email'), t('sysAdmin.global.role')]} rows={rows} label={t('sysPages.sections.global-roles')} />
      </div>
    </SystemPage>
  );
}
